import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plane,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  MapPin,
  AlertTriangle,
  Check,
  ChevronRight,
  ShieldCheck,
  Download,
  Edit3,
  Trash2,
  Save,
  Eye,
  Receipt,
  RotateCcw,
  RefreshCw,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EDITABLE_STATUSES = new Set(['draft', 'submitted', 'pending_approval']);
const DELETABLE_STATUSES = new Set(['draft', 'submitted', 'pending_approval', 'returned_for_correction', 'sent_back', 'cancelled']);
const RECEIPT_ELIGIBLE_STATUSES = new Set(['submitted', 'pending_approval', 'approved', 'booked', 'completed', 'finance_approved', 'rejected']);

import SelfBookingClaimModal from '../components/travel/SelfBookingClaimModal';
import ManagerApprovalSelector from '../components/travel/ManagerApprovalSelector';

const initialFormState = {
  purpose: '',
  travel_type: 'flight',
  travel_category: 'Business Travel',
  project: '',
  client: '',
  cost_center: '',
  estimated_budget: 0,
  advance_requested: false,
  advance_hotel: 0,
  advance_food: 0,
  advance_local_travel: 0,
  advance_misc: 0,
  journey_details: [
    { from_city: '', to_city: '', departure_date: new Date().toISOString().split('T')[0], return_date: '', preferred_time: 'Morning', flexible_dates: false },
  ],
  booking_preferences: {
    flight_class: 'economy',
    train_class: '3ac',
  },
  passengers: [
    { name: 'Self', age: 30, gender: 'Male', id_number: '', is_guest: false },
  ],
};

const ESSTravelRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [essTab, setEssTab] = useState('my_claims'); // 'my_claims' | 'raised_for_others'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'pending' | 'sent_back' | 'approved' | 'rejected' | 'draft'
  const [showModal, setShowModal] = useState(false);
  const [showSelfBookingModal, setShowSelfBookingModal] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // holds the req object
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingReqId, setEditingReqId] = useState(null); // null = create, string = edit mode

  // Rework and Reapplication Workflow States
  const [reworkMode, setReworkMode] = useState({ isRework: false, reqId: null, returnRemarks: '', sentBackBy: '', requestNumber: '' });
  const [reapplicationMode, setReapplicationMode] = useState({ isReapplication: false, rejectedFromId: '', requestNumber: '', rejectionReason: '' });
  const [reworkNotes, setReworkNotes] = useState('');

  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  // Approval Manager State
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [loadingManagers, setLoadingManagers] = useState(false);

  const fetchEligibleManagers = async (targetEmpId) => {
    try {
      setLoadingManagers(true);
      const url = targetEmpId
        ? `${API_URL}/approval-workflows/eligible-managers?employee_id=${targetEmpId}&doc_type=travel_request`
        : `${API_URL}/approval-workflows/eligible-managers?doc_type=travel_request`;
      const { data } = await axios.get(url, { withCredentials: true });
      const mgrList = data?.managers || [];
      setEligibleManagers(mgrList);
      if (mgrList.length > 0) {
        const defaultMgr = mgrList.find(m => m.hierarchy_level === 1 || m.relationship_to_employee === 'Reporting Manager') || mgrList[0];
        setSelectedApproverId(prev => (prev && mgrList.some(m => m.employee_id === prev) ? prev : defaultMgr.employee_id));
      }
    } catch (err) {
      console.error('Failed to fetch eligible managers:', err);
    } finally {
      setLoadingManagers(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      fetchEligibleManagers(formData.beneficiary_employee_id || formData.employee_id);
    }
  }, [showModal, formData.beneficiary_employee_id, formData.employee_id]);

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests`, { withCredentials: true });
      setRequests((data.requests || []).filter(r => r.status !== 'deleted'));
    } catch (err) {
      toast.error('Failed to load travel requests');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (updater) => {
    setIsDirty(true);
    if (typeof updater === 'function') {
      setFormData(updater);
    } else {
      setFormData(updater);
    }
  };

  const validateCurrentStep = () => {
    if (formStep === 1) {
      if (!formData.purpose.trim()) { toast.error('Please specify Travel Purpose.'); return false; }
      for (let i = 0; i < formData.journey_details.length; i++) {
        const leg = formData.journey_details[i];
        if (!leg.from_city.trim() || !leg.to_city.trim() || !leg.departure_date) {
          toast.error(`Please complete From City, To City, and Departure Date for Leg ${i + 1}.`);
          return false;
        }
      }
    }
    if (formStep === 2) {
      if (!formData.passengers?.length) { toast.error('Please add at least one passenger.'); return false; }
      for (let i = 0; i < formData.passengers.length; i++) {
        if (!formData.passengers[i].name?.trim()) { toast.error(`Passenger ${i + 1} name is required.`); return false; }
      }
    }
    return true;
  };

  const handleNextStep = () => { if (validateCurrentStep()) setFormStep(prev => Math.min(5, prev + 1)); };
  const handlePreviousStep = () => setFormStep(prev => Math.max(1, prev - 1));

  const handleSaveDraft = async () => {
    try {
      setSubmitting(true);
      const payload = { ...formData, selected_approver_id: selectedApproverId, status: 'draft' };
      if (editingReqId) {
        await axios.patch(`${API_URL}/travel-desk/requests/${editingReqId}`, payload, { withCredentials: true });
        toast.success('Travel Request draft updated!');
      } else {
        await axios.post(`${API_URL}/travel-desk/requests`, payload, { withCredentials: true });
        toast.success('Travel Request Draft saved!');
      }
      setIsDirty(false);
      setShowModal(false);
      setShowConfirmClose(false);
      setEditingReqId(null);
      fetchMyRequests();
    } catch (err) {
      toast.error('Failed to save draft.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!validateCurrentStep()) return;
    try {
      setSubmitting(true);
      const payload = { ...formData, selected_approver_id: selectedApproverId, status: 'submitted' };

      if (reworkMode.isRework && reworkMode.reqId) {
        payload.rework_notes = reworkNotes || 'Request updated and resubmitted after rework';
        await axios.post(`${API_URL}/travel-desk/requests/${reworkMode.reqId}/resubmit`, payload, { withCredentials: true });
        toast.success(`Travel Request ${reworkMode.requestNumber} resubmitted successfully!`);
      } else if (reapplicationMode.isReapplication && reapplicationMode.rejectedFromId) {
        payload.reapplied_from_id = reapplicationMode.rejectedFromId;
        payload.reapplied_from_request_number = reapplicationMode.requestNumber;
        payload.reapply_notes = `Reapplied from rejected request ${reapplicationMode.requestNumber}`;
        await axios.post(`${API_URL}/travel-desk/requests/${reapplicationMode.rejectedFromId}/reapply`, payload, { withCredentials: true });
        toast.success('New Travel Request submitted successfully from reapplication!');
      } else if (editingReqId) {
        // EDIT: PATCH existing record
        await axios.patch(`${API_URL}/travel-desk/requests/${editingReqId}`, payload, { withCredentials: true });
        toast.success('Travel Request updated and submitted!');
      } else {
        await axios.post(`${API_URL}/travel-desk/requests`, payload, { withCredentials: true });
        toast.success('Travel Request submitted successfully!');
      }

      setIsDirty(false);
      setShowModal(false);
      setEditingReqId(null);
      setReworkMode({ isRework: false, reqId: null, returnRemarks: '', sentBackBy: '', requestNumber: '' });
      setReapplicationMode({ isReapplication: false, rejectedFromId: '', requestNumber: '', rejectionReason: '' });
      setReworkNotes('');
      fetchMyRequests();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit travel request.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      setShowModal(false);
      setEditingReqId(null);
      setReworkMode({ isRework: false, reqId: null, returnRemarks: '', sentBackBy: '', requestNumber: '' });
      setReapplicationMode({ isReapplication: false, rejectedFromId: '', requestNumber: '', rejectionReason: '' });
      setReworkNotes('');
    }
  };

  const handleDiscardChanges = () => {
    setShowConfirmClose(false);
    setShowModal(false);
    setIsDirty(false);
    setEditingReqId(null);
    setReworkMode({ isRework: false, reqId: null, returnRemarks: '', sentBackBy: '', requestNumber: '' });
    setReapplicationMode({ isReapplication: false, rejectedFromId: '', requestNumber: '', rejectionReason: '' });
    setReworkNotes('');
    setFormData(initialFormState);
  };

  // Open wizard in EDIT mode — pre-populate all form fields
  const handleOpenEdit = (req) => {
    const adv = req.advance_details || {};
    const preload = {
      travel_type: req.travel_type || 'flight',
      purpose: req.purpose || '',
      project: req.project || req.project_name || '',
      client: req.client || '',
      travel_category: req.travel_category || 'Business Travel',
      booking_responsibility: req.booking_responsibility || 'company_booked',
      is_international: req.is_international || false,
      is_emergency: req.is_emergency || false,
      estimated_budget: req.estimated_budget || 0,
      cost_center: req.cost_center || '',
      gl_account: req.gl_account || '',
      department_id: req.department_id || '',
      department_name: req.department_name || req.department || '',
      project_id: req.project_id || '',
      project_code: req.project_code || '',
      project_name: req.project_name || req.project || '',
      remarks: req.remarks || '',
      attachments: req.attachments || [],
      advance_requested: !!req.advance_requested,
      advance_hotel: req.advance_hotel || adv.hotel_amount || 0,
      advance_food: req.advance_food || adv.food_amount || 0,
      advance_local_travel: req.advance_local_travel || adv.local_travel_amount || 0,
      advance_misc: req.advance_misc || adv.misc_amount || 0,
      journey_details: req.journey_details?.length
        ? req.journey_details
        : initialFormState.journey_details,
      booking_preferences: req.booking_preferences || initialFormState.booking_preferences,
      passengers: req.passengers?.length ? req.passengers : initialFormState.passengers,
    };
    setSelectedApproverId(req.selected_approver_id || req.selected_manager_id || null);
    setFormData(preload);
    setEditingReqId(req._id);
    setFormStep(1);
    setIsDirty(false);
    setShowModal(true);
  };

  // Open wizard in REWORK mode — pre-populate and set rework metadata
  const handleOpenRework = (req) => {
    handleOpenEdit(req);
    setReworkMode({
      isRework: true,
      reqId: req._id,
      returnRemarks: req.return_remarks || 'Returned for correction',
      sentBackBy: req.sent_back_by || 'Approving Manager',
      requestNumber: req.request_number,
    });
    setReapplicationMode({ isReapplication: false, rejectedFromId: '', requestNumber: '', rejectionReason: '' });
    setReworkNotes(req.rework_notes || '');
    toast.info(`Rework Mode: Updating request ${req.request_number}`);
  };

  // Open wizard in REAPPLY mode — brand new request pre-filled from rejected claim
  const handleOpenReapply = (req) => {
    handleOpenEdit(req);
    setEditingReqId(null); // Will create a new document!
    setReapplicationMode({
      isReapplication: true,
      rejectedFromId: req._id,
      requestNumber: req.request_number,
      rejectionReason: req.rejection_reason || 'Rejected by Manager / Approver',
    });
    setReworkMode({ isRework: false, reqId: null, returnRemarks: '', sentBackBy: '', requestNumber: '' });
    setReworkNotes('');
    toast.info(`Reapplication Mode: Pre-filling from rejected request ${req.request_number}`);
  };

  // Cancel / Withdraw submitted or sent back request
  const handleCancelRequest = async (req) => {
    if (!window.confirm(`Are you sure you want to cancel and withdraw Travel Request ${req.request_number}?`)) return;
    try {
      await axios.post(`${API_URL}/travel-desk/requests/${req._id}/cancel`, { remarks: 'Cancelled by employee' }, { withCredentials: true });
      toast.success(`Travel Request ${req.request_number} cancelled.`);
      fetchMyRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel travel request.');
    }
  };

  // SOFT DELETE
  const handleConfirmDelete = async (req) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/requests/${req._id}`, { withCredentials: true });
      toast.success(`Travel Request ${req.request_number} deleted.`);
      setShowDeleteConfirm(null);
      fetchMyRequests();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to delete.';
      toast.error(msg);
      setShowDeleteConfirm(null);
    }
  };

  // DOWNLOAD RECEIPT
  const handleDownloadReceipt = async (req) => {
    setDownloadingId(req._id);
    try {
      const resp = await axios.get(`${API_URL}/travel-desk/requests/${req._id}/receipt`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TravelReceipt_${req.request_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Travel Approval Receipt downloaded!');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to download receipt.';
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const addJourneyLeg = () => {
    updateFormData(prev => ({
      ...prev,
      journey_details: [
        ...prev.journey_details,
        { from_city: '', to_city: '', departure_date: new Date().toISOString().split('T')[0], return_date: '', preferred_time: 'Morning', flexible_dates: false },
      ],
    }));
  };

  const removeJourneyLeg = (idx) => {
    if (formData.journey_details.length <= 1) return;
    updateFormData(prev => ({ ...prev, journey_details: prev.journey_details.filter((_, i) => i !== idx) }));
  };

  const addPassenger = () => {
    updateFormData(prev => ({ ...prev, passengers: [...prev.passengers, { name: '', age: 25, gender: 'Male', id_number: '', is_guest: false }] }));
  };

  const removePassenger = (idx) => {
    if (formData.passengers.length <= 1) return;
    updateFormData(prev => ({ ...prev, passengers: prev.passengers.filter((_, i) => i !== idx) }));
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'returned_for_correction' || s === 'sent_back' || s === 'rework') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1 shadow-2xs">
          <RotateCcw className="w-2.5 h-2.5" /> Sent Back / Rework
        </span>
      );
    }
    if (s === 'submitted' || s === 'pending_approval' || s === 'in_progress') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-900 border border-blue-200 flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" /> Pending Approval
        </span>
      );
    }
    if (s === 'approved') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-200 flex items-center gap-1">
          <CheckCircle className="w-2.5 h-2.5" /> Approved
        </span>
      );
    }
    if (s === 'finance_approved') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-900 border border-indigo-200">
          Finance Approved
        </span>
      );
    }
    if (s === 'booked') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-200 flex items-center gap-1">
          <ShieldCheck className="w-2.5 h-2.5" /> Ticket Booked
        </span>
      );
    }
    if (s === 'rejected') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-900 border border-rose-200 flex items-center gap-1">
          <XCircle className="w-2.5 h-2.5" /> Rejected
        </span>
      );
    }
    if (s === 'cancelled') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-300">
          Cancelled
        </span>
      );
    }
    if (s === 'completed') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-900 border border-teal-200 flex items-center gap-1">
          <Check className="w-2.5 h-2.5" /> Completed
        </span>
      );
    }
    if (s === 'draft') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-300">
          Draft
        </span>
      );
    }
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 capitalize border border-slate-200">{status}</span>;
  };

  const matchesStatusFilter = (req, key) => {
    if (key === 'all') return true;
    const st = (req.normalized_status || req.approval_status || req.status || '').toLowerCase();
    if (key === 'pending') return ['submitted', 'pending_approval', 'in_progress', 'pending'].includes(st);
    if (key === 'sent_back') return ['returned_for_correction', 'sent_back', 'rework'].includes(st);
    if (key === 'approved') return ['approved', 'finance_approved', 'booked', 'completed'].includes(st);
    if (key === 'rejected') return ['rejected'].includes(st);
    if (key === 'draft') return ['draft'].includes(st);
    return st === key;
  };

  const filteredRequests = requests
    .filter((r) => {
      if (essTab === 'raised_for_others') return r.requested_on_behalf;
      if (essTab === 'booked_tickets') return r.booking_details || r.booking_responsibility === 'company_booked';
      if (essTab === 'travel_advances') return r.advance_requested || (r.advances && r.advances.length > 0);
      if (essTab === 'daily_expenses') return (r.daily_expenses && r.daily_expenses.length > 0) || r.settlement;
      if (essTab === 'extensions') return r.is_extended || (r.extension_history && r.extension_history.length > 0);
      if (essTab === 'reimbursements') return (r.settlement && (r.settlement.company_payable > 0 || r.settlement.employee_refund > 0));
      return !r.requested_on_behalf;
    })
    .filter((r) => matchesStatusFilter(r, filterStatus));

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Plane className="w-7 h-7 text-amber-400" /> My Travel Desk Requests</h1>
          <p className="text-xs text-blue-200 mt-1">Submit and track corporate travel requests, ticket bookings, and approval status.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSelfBookingModal(true)}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2 text-sm transition-all"
          >
            <Receipt className="w-4 h-4 text-emerald-300" /> Self Booking Claim
          </button>
          <button
            onClick={() => {
              setFormData(initialFormState);
              setFormStep(1);
              setIsDirty(false);
              setEditingReqId(null);
              setReworkMode({ isRework: false, reqId: null, returnRemarks: '', sentBackBy: '', requestNumber: '' });
              setReapplicationMode({ isReapplication: false, rejectedFromId: '', requestNumber: '', rejectionReason: '' });
              setReworkNotes('');
              setShowModal(true);
            }}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2 text-sm transition-all"
          >
            <Plus className="w-4 h-4" /> Raise Travel Request
          </button>
        </div>
      </div>

      {/* Tab Filter & Status Filter Bars */}
      <div className="space-y-3">
        <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex flex-wrap items-center gap-2">
          {[
            { id: 'my_claims', label: '1. My Travel Requests' },
            { id: 'booked_tickets', label: '2. Booked Tickets' },
            { id: 'travel_advances', label: '3. Travel Advances' },
            { id: 'daily_expenses', label: '4. Daily Expenses' },
            { id: 'extensions', label: '5. Travel Extensions' },
            { id: 'reimbursements', label: '6. Reimbursements & Refunds' },
            { id: 'raised_for_others', label: '7. Raised for Others' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setEssTab(tab.id)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                essTab === tab.id ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Status Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="status-filter-pills">
          {[
            { key: 'all', label: 'All Requests' },
            { key: 'pending', label: 'Pending Approval' },
            { key: 'sent_back', label: 'Sent Back / Rework' },
            { key: 'approved', label: 'Approved' },
            { key: 'rejected', label: 'Rejected' },
            { key: 'draft', label: 'Draft' },
          ].map((tab) => {
            const active = filterStatus === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilterStatus(tab.key)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 ${
                  active
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'
                }`}
                data-testid={`filter-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            {essTab === 'my_claims' ? 'My Beneficiary Travel History & Status' : 'Travel Requests Raised On-Behalf of Others'}
          </h3>
          <div className="text-xs text-slate-500 font-medium">Total: <b>{filteredRequests.length}</b></div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Loading travel requests...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-12 text-center space-y-3 text-slate-400">
            <Plane className="w-12 h-12 mx-auto text-slate-300 stroke-[1.5]" />
            <div className="text-sm font-bold text-slate-600">
              {filterStatus !== 'all' ? `No Travel Requests matching "${filterStatus.replace('_', ' ')}"` : essTab === 'my_claims' ? 'No Personal Travel Requests Found' : 'No On-Behalf Travel Requests Raised For Others'}
            </div>
            <p className="text-xs max-w-sm mx-auto">Click "Raise Travel Request" above to begin.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredRequests.map((req) => (
              <div key={req._id} className="p-5 hover:bg-slate-50 transition-colors space-y-3">
                {/* Row Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200">{req.request_number}</span>
                    <span className="font-bold text-slate-900 text-sm capitalize">{req.purpose} <span className="text-slate-400 font-normal">({req.travel_type})</span></span>
                    {req.is_international && <span className="text-[10px] font-bold uppercase bg-purple-100 text-purple-800 px-2 py-0.5 rounded">International</span>}
                    {req.is_emergency && <span className="text-[10px] font-bold uppercase bg-rose-100 text-rose-800 px-2 py-0.5 rounded">Emergency</span>}
                    {(req.is_reapplied || req.reapplied_from_id) && (
                      <span className="text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded flex items-center gap-1">
                        <RefreshCw className="w-2.5 h-2.5" /> Reapplied Claim
                      </span>
                    )}
                    {req.requested_on_behalf && req.requester_name && (
                      <span className="text-[10px] font-bold bg-indigo-50 text-indigo-800 border border-indigo-200 px-2 py-0.5 rounded">
                        Requested on your behalf by: {req.requester_name}
                      </span>
                    )}
                    {req.beneficiary_name && req.requested_on_behalf && (
                      <span className="text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded">
                        Beneficiary: {req.beneficiary_name} ({req.beneficiary_employee_code})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {getStatusBadge(req.status)}
                  </div>
                </div>

                {/* Return Remarks Callout for Sent Back */}
                {(req.status === 'returned_for_correction' || req.status === 'sent_back' || req.approval_status === 'sent_back') && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs flex items-start gap-2.5">
                    <RotateCcw className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-amber-900">
                        Sent Back for Correction {req.sent_back_by ? `by ${req.sent_back_by}` : ''}
                      </div>
                      <p className="text-amber-800 mt-0.5 font-medium">{req.return_remarks || 'Returned for correction. Please update and resubmit.'}</p>
                    </div>
                  </div>
                )}

                {/* Rejection Reason Callout for Rejected */}
                {(req.status === 'rejected' || req.approval_status === 'rejected') && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs flex items-start gap-2.5">
                    <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-rose-900">
                        Rejected {req.rejected_by ? `by ${req.rejected_by}` : ''}
                      </div>
                      <p className="text-rose-800 mt-0.5 font-medium">{req.rejection_reason || 'Travel request rejected by approver.'}</p>
                    </div>
                  </div>
                )}

                {/* Employee Rework Notes */}
                {req.rework_notes && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-2.5 text-xs text-blue-900">
                    <span className="font-bold">Rework Notes:</span> {req.rework_notes}
                  </div>
                )}

                {/* Meta info grid */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Route</div>
                    <div className="font-bold text-slate-800 mt-0.5">{req.journey_details?.[0]?.from_city || '—'} → {req.journey_details?.[0]?.to_city || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Travel Dates</div>
                    <div className="font-bold text-slate-800 mt-0.5">{req.journey_details?.[0]?.departure_date} to {req.journey_details?.[0]?.return_date || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Project / Client</div>
                    <div className="font-semibold text-slate-700 mt-0.5">{req.project || 'Internal'} / {req.client || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Est. Budget</div>
                    <div className="font-mono font-bold text-emerald-700 mt-0.5">₹ {(req.estimated_budget || 0).toLocaleString('en-IN')}</div>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-slate-400 font-semibold">Step:</span>
                    <span className="capitalize font-bold text-slate-700 bg-slate-200 px-2 py-0.5 rounded text-[10px]">{req.current_approval_step?.replace(/_/g, ' ') || 'Pending'}</span>
                    {(req.status === 'approved' || req.status === 'booked' || req.ticket_booking_status) && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                        req.ticket_booking_status === 'not_booked' || req.booking_status === 'not_booked'
                          ? 'bg-amber-50 text-amber-900 border-amber-300'
                          : req.ticket_booking_status === 'booked' || req.status === 'booked'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                          : 'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>
                        {req.ticket_booking_status === 'not_booked' || req.booking_status === 'not_booked'
                          ? 'Ticket Not Booked'
                          : req.ticket_booking_status === 'booked' || req.status === 'booked'
                          ? 'Ticket Booked'
                          : 'Ticket Pending'}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* View Details */}
                    <button
                      onClick={() => setSelectedRequest(req)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-[11px] transition-all"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>

                    {/* Rework / Resubmit for Sent Back requests */}
                    {(req.status === 'returned_for_correction' || req.status === 'sent_back' || req.approval_status === 'sent_back') && (
                      <button
                        onClick={() => handleOpenRework(req)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold text-[11px] shadow-xs transition-all"
                      >
                        <RotateCcw className="w-3 h-3" /> Rework / Resubmit
                      </button>
                    )}

                    {/* Reapply for Rejected requests */}
                    {(req.status === 'rejected' || req.approval_status === 'rejected') && (
                      <button
                        onClick={() => handleOpenReapply(req)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold text-[11px] shadow-xs transition-all"
                      >
                        <RefreshCw className="w-3 h-3" /> Reapply
                      </button>
                    )}

                    {/* Edit — for editable draft or submitted */}
                    {EDITABLE_STATUSES.has(req.status) && req.status !== 'returned_for_correction' && req.status !== 'sent_back' && (
                      <button
                        onClick={() => handleOpenEdit(req)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg font-bold text-[11px] transition-all"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                    )}

                    {/* Cancel / Withdraw — for pending or sent back */}
                    {(req.status === 'submitted' || req.status === 'pending_approval' || req.status === 'returned_for_correction' || req.status === 'sent_back' || req.approval_status === 'sent_back' || req.approval_status === 'in_progress') && (
                      <button
                        onClick={() => handleCancelRequest(req)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-[11px] transition-all"
                      >
                        <Undo2 className="w-3 h-3" /> Cancel
                      </button>
                    )}

                    {/* Delete — only for deletable statuses */}
                    {DELETABLE_STATUSES.has(req.status) && (
                      <button
                        onClick={() => setShowDeleteConfirm(req)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg font-bold text-[11px] transition-all"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}

                    {/* Download Complete Travel & Settlement Dossier PDF */}
                    {RECEIPT_ELIGIBLE_STATUSES.has(req.status) && (
                      <button
                        onClick={() => handleDownloadReceipt(req)}
                        disabled={downloadingId === req._id}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg font-bold text-[11px] transition-all disabled:opacity-60"
                        title="Download complete Travel & Settlement Dossier PDF"
                      >
                        <Download className="w-3 h-3" />
                        {downloadingId === req._id ? 'Generating...' : 'PDF Dossier'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TRAVEL REQUEST WIZARD MODAL (Create / Edit) */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col max-h-[92vh]">
            {/* Top bar */}
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-900 text-white flex items-center justify-between shrink-0">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Plane className="w-5 h-5 text-amber-400" />
                {reworkMode.isRework
                  ? `Rework & Resubmit Travel Request (${reworkMode.requestNumber})`
                  : reapplicationMode.isReapplication
                  ? `Reapply Travel Request (from #${reapplicationMode.requestNumber})`
                  : editingReqId
                  ? 'Edit Travel Request'
                  : 'Submit New Corporate Travel Request'}
              </h3>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-white font-bold text-xl leading-none">&times;</button>
            </div>

            {/* Rework Mode Banner */}
            {reworkMode.isRework && (
              <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-2.5 text-xs text-amber-900 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>
                    <b>Rework Mode ({reworkMode.requestNumber}):</b> {reworkMode.returnRemarks}
                    {reworkMode.sentBackBy && <span className="text-amber-700 font-normal"> (Sent back by {reworkMode.sentBackBy})</span>}
                  </span>
                </div>
                <button type="button" onClick={handleDiscardChanges} className="text-amber-800 hover:underline font-bold text-[11px]">Cancel Rework</button>
              </div>
            )}

            {/* Reapplication Mode Banner */}
            {reapplicationMode.isReapplication && (
              <div className="bg-purple-500/10 border-b border-purple-500/20 px-6 py-2.5 text-xs text-purple-900 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-purple-600 shrink-0" />
                  <span>
                    <b>Reapplication Mode:</b> Creating new request from rejected claim #{reapplicationMode.requestNumber}.
                    {reapplicationMode.rejectionReason && <span className="text-purple-700 font-normal"> Reason: "{reapplicationMode.rejectionReason}"</span>}
                  </span>
                </div>
                <button type="button" onClick={handleDiscardChanges} className="text-purple-800 hover:underline font-bold text-[11px]">Cancel Reapply</button>
              </div>
            )}

            {/* Step indicator */}
            <div className="px-4 py-2.5 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 overflow-x-auto shrink-0 text-xs">
              {[
                { step: 1, label: '1. Travel Details' },
                { step: 2, label: '2. Passengers' },
                { step: 3, label: '3. Preferences' },
                { step: 4, label: '4. Remarks' },
                { step: 5, label: '5. Review & Submit' },
              ].map((s) => (
                <div key={s.step} className="flex items-center gap-1 shrink-0">
                  <div className={`px-3 py-1 rounded-full font-bold transition-colors ${formStep === s.step ? 'bg-blue-600 text-white shadow-sm' : formStep > s.step ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                    {s.label}
                  </div>
                  {s.step < 5 && <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                </div>
              ))}
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto space-y-5 text-xs flex-1">

              {/* STEP 1: TRAVEL DETAILS */}
              {formStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Travel Mode *</label>
                      <select
                        value={formData.travel_type}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const nextPref = {};
                          if (newType === 'flight') {
                            nextPref.flight_class = 'economy';
                          } else if (newType === 'train') {
                            nextPref.train_class = formData.booking_preferences?.train_class === 'sleeper' ? 'sleeper' : '3ac';
                          }
                          updateFormData({
                            ...formData,
                            travel_type: newType,
                            booking_preferences: nextPref,
                          });
                        }}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none"
                      >
                        <option value="flight">Flight Ticket</option>
                        <option value="train">Train Ticket</option>
                        <option value="bus">Bus Ticket</option>
                        <option value="cab">Cab Booking</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Travel Category</label>
                      <select value={formData.travel_category} onChange={e => updateFormData({ ...formData, travel_category: e.target.value, is_international: e.target.value === 'International' })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none">
                        <option value="Domestic">Domestic</option>
                        <option value="International">International</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Travel Purpose *</label>
                      <input type="text" value={formData.purpose} onChange={e => updateFormData({ ...formData, purpose: e.target.value })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none" placeholder="e.g. Client Architecture Workshop" required />
                    </div>
                  </div>

                  <ManagerApprovalSelector
                    selectedApproverId={selectedApproverId}
                    onSelectApprover={(empId, mgrObj) => setSelectedApproverId(empId)}
                    eligibleManagers={eligibleManagers}
                    targetEmployeeId={formData.beneficiary_employee_id || ''}
                    loadingManagers={loadingManagers}
                    docType="travel_request"
                    label="Manager Approval Selection (Outstation Conveyance) *"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Project Name</label>
                      <input type="text" value={formData.project} onChange={e => updateFormData({ ...formData, project: e.target.value })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none" />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Client Name</label>
                      <input type="text" value={formData.client} onChange={e => updateFormData({ ...formData, client: e.target.value })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">Journey Legs &amp; Route *</h4>
                      <button type="button" onClick={addJourneyLeg} className="text-xs font-bold text-blue-600 hover:underline">+ Add City / Leg</button>
                    </div>
                    {formData.journey_details.map((leg, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-slate-700 text-[10px]">LEG {idx + 1}</span>
                          {formData.journey_details.length > 1 && (
                            <button type="button" onClick={() => removeJourneyLeg(idx)} className="text-rose-600 font-bold hover:underline text-[10px] flex items-center gap-0.5"><Trash2 className="w-3 h-3" />Remove</button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {['from_city', 'to_city', 'departure_date', 'return_date'].map((field) => (
                            <div key={field}>
                              <label className="block text-[10px] font-bold text-slate-500 capitalize">{field.replace('_', ' ')}{field === 'from_city' || field === 'to_city' || field === 'departure_date' ? ' *' : ''}</label>
                              <input
                                type={field.includes('date') ? 'date' : 'text'}
                                value={leg[field] || ''}
                                onChange={e => {
                                  const nl = [...formData.journey_details];
                                  nl[idx][field] = e.target.value;
                                  updateFormData({ ...formData, journey_details: nl });
                                }}
                                className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs"
                                required={['from_city', 'to_city', 'departure_date'].includes(field)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 2: PASSENGERS */}
              {formStep === 2 && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">Passenger Manifest *</h4>
                    <button type="button" onClick={addPassenger} className="text-xs font-bold text-blue-600 hover:underline">+ Add Passenger</button>
                  </div>
                  {formData.passengers.map((p, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-700 text-[10px]">PASSENGER {idx + 1}</span>
                        {formData.passengers.length > 1 && (
                          <button type="button" onClick={() => removePassenger(idx)} className="text-rose-600 font-bold hover:underline text-[10px] flex items-center gap-0.5"><Trash2 className="w-3 h-3" />Remove</button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">Full Name *</label>
                          <input type="text" value={p.name} onChange={e => { const np=[...formData.passengers]; np[idx].name=e.target.value; updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs" required />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">Age</label>
                          <input type="number" value={p.age} onChange={e => { const np=[...formData.passengers]; np[idx].age=Number(e.target.value); updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">Gender</label>
                          <select value={p.gender} onChange={e => { const np=[...formData.passengers]; np[idx].gender=e.target.value; updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs">
                            <option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">ID / Passport No</label>
                          <input type="text" value={p.id_number} onChange={e => { const np=[...formData.passengers]; np[idx].id_number=e.target.value; updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* STEP 3: PREFERENCES */}
              {formStep === 3 && (
                <div className="space-y-4">
                  {formData.travel_type === 'flight' && (
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Preferred Flight Class *</label>
                      <select
                        value={formData.booking_preferences?.flight_class || 'economy'}
                        onChange={(e) => updateFormData({ ...formData, booking_preferences: { flight_class: e.target.value } })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none"
                      >
                        <option value="economy">Economy Class</option>
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">Company policy permits Economy Class only for corporate flights.</p>
                    </div>
                  )}

                  {formData.travel_type === 'train' && (
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Preferred Train Tier *</label>
                      <select
                        value={formData.booking_preferences?.train_class || '3ac'}
                        onChange={(e) => updateFormData({ ...formData, booking_preferences: { train_class: e.target.value } })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none"
                      >
                        <option value="3ac">3 AC Tier</option>
                        <option value="sleeper">Sleeper Class</option>
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">Company policy permits 3 AC Tier and Sleeper Class only for train travel.</p>
                    </div>
                  )}

                  {formData.travel_type !== 'flight' && formData.travel_type !== 'train' && (
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-slate-600 text-xs">
                      No specific class entitlement preferences required for <b className="capitalize">{formData.travel_type}</b> travel mode.
                    </div>
                  )}

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Estimated Trip Budget (₹) *</label>
                    <input
                      type="number"
                      value={formData.estimated_budget}
                      onChange={(e) => updateFormData({ ...formData, estimated_budget: Number(e.target.value) })}
                      className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold outline-none"
                      required
                    />
                  </div>
                </div>
              )}

              {/* STEP 4: REMARKS */}
              {formStep === 4 && (
                <div className="space-y-4">
                  {reworkMode.isRework && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 space-y-2">
                      <label className="block font-bold text-amber-900">
                        Employee Rework Notes / Changes Made *
                      </label>
                      <p className="text-[10px] text-amber-700">
                        Manager Return Remarks: "{reworkMode.returnRemarks}"
                      </p>
                      <textarea
                        rows={3}
                        value={reworkNotes}
                        onChange={(e) => setReworkNotes(e.target.value)}
                        placeholder="Explain the changes or corrections you made for the manager's review..."
                        className="w-full p-2.5 bg-white border border-amber-300 rounded-xl outline-none text-xs"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Special Instructions &amp; Remarks</label>
                    <textarea rows={4} value={formData.remarks} onChange={e => updateFormData({...formData, remarks:e.target.value})} placeholder="e.g. Prefer morning flight, aisle seat preference, non-smoking hotel..." className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl outline-none" />
                  </div>
                </div>
              )}

              {/* STEP 5: REVIEW */}
              {formStep === 5 && (
                <div className="space-y-4">
                  <div className={`p-3 rounded-xl text-xs flex items-center gap-2 border ${
                    reworkMode.isRework
                      ? 'bg-amber-50 border-amber-200 text-amber-900'
                      : reapplicationMode.isReapplication
                      ? 'bg-purple-50 border-purple-200 text-purple-900'
                      : 'bg-blue-50 border-blue-200 text-blue-900'
                  }`}>
                    {reworkMode.isRework ? (
                      <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
                    ) : reapplicationMode.isReapplication ? (
                      <RefreshCw className="w-4 h-4 text-purple-600 shrink-0" />
                    ) : (
                      <CheckCircle className="w-4 h-4 text-blue-600 shrink-0" />
                    )}
                    <span>
                      {reworkMode.isRework
                        ? `Review corrected request details before resubmitting for manager approval.`
                        : reapplicationMode.isReapplication
                        ? `Review newly drafted claim details before submitting reapplication.`
                        : `Review all details below before final ${editingReqId ? 'update' : 'submission'}.`}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <div><b>Travel Mode:</b> <span className="capitalize">{formData.travel_type.replace('_', ' ')}</span></div>
                    <div><b>Category:</b> {formData.travel_category}</div>
                    <div><b>Purpose:</b> {formData.purpose}</div>
                    <div><b>Project / Client:</b> {formData.project} / {formData.client}</div>
                    <div><b>Est. Budget:</b> <span className="font-mono font-bold text-emerald-700">₹ {Number(formData.estimated_budget).toLocaleString('en-IN')}</span></div>
                    <div><b>Passengers:</b> {formData.passengers.length} Person(s)</div>
                  </div>
                  <div className="space-y-1">
                    <h5 className="font-bold text-slate-800 uppercase text-[10px]">Journey Route</h5>
                    {formData.journey_details.map((leg, idx) => (
                      <div key={idx} className="p-2 bg-white rounded-lg border border-slate-200 flex justify-between text-xs">
                        <span><b>Leg {idx + 1}:</b> {leg.from_city} → {leg.to_city}</span>
                        <span><b>Departure:</b> {leg.departure_date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Action Bar */}
            <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-between items-center shrink-0">
              {formStep > 1 ? (
                <button type="button" onClick={handlePreviousStep} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold transition-all">← Previous</button>
              ) : <div />}

              <div className="flex gap-2.5 items-center">
                <button type="button" onClick={handleSaveDraft} disabled={submitting} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all shadow-sm disabled:opacity-50">
                  <Save className="w-3.5 h-3.5 text-amber-400" /> Save Draft
                </button>
                {formStep < 5 ? (
                  <button type="button" onClick={handleNextStep} className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-md transition-all">
                    Next Step →
                  </button>
                ) : reworkMode.isRework ? (
                  <button type="button" onClick={handleFinalSubmit} disabled={submitting} className="px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-extrabold flex items-center gap-1.5 shadow-lg transition-all disabled:opacity-50">
                    <RotateCcw className="w-4 h-4" /> Resubmit Reworked Request
                  </button>
                ) : reapplicationMode.isReapplication ? (
                  <button type="button" onClick={handleFinalSubmit} disabled={submitting} className="px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-extrabold flex items-center gap-1.5 shadow-lg transition-all disabled:opacity-50">
                    <RefreshCw className="w-4 h-4" /> Submit Reapplication
                  </button>
                ) : (
                  <button type="button" onClick={handleFinalSubmit} disabled={submitting} className="px-6 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl font-extrabold flex items-center gap-1.5 shadow-lg transition-all disabled:opacity-50">
                    <Check className="w-4 h-4" /> {editingReqId ? 'Update & Submit Request' : 'Submit Travel Request'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UNSAVED CHANGES CONFIRMATION */}
      {showConfirmClose && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-amber-600"><AlertTriangle className="w-6 h-6 shrink-0" /><h3 className="font-bold text-slate-900 text-base">Unsaved Changes</h3></div>
            <p className="text-xs text-slate-600 leading-relaxed">You have unsaved changes. Do you want to Save Draft before leaving?</p>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 text-xs font-bold">
              <button onClick={() => setShowConfirmClose(false)} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl">Cancel</button>
              <button onClick={handleDiscardChanges} className="px-3 py-2 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded-xl">Discard</button>
              <button onClick={handleSaveDraft} className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl shadow-md">Save Draft</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-rose-600"><Trash2 className="w-6 h-6 shrink-0" /><h3 className="font-bold text-slate-900 text-base">Delete Travel Request?</h3></div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to delete Travel Request <b>{showDeleteConfirm.request_number}</b>?<br/>
              The record will be soft-deleted and retained in audit logs.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 text-xs font-bold">
              <button onClick={() => setShowDeleteConfirm(null)} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl">Cancel</button>
              <button onClick={() => handleConfirmDelete(showDeleteConfirm)} className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl shadow-md">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW DETAIL / TIMELINE MODAL */}
      {selectedRequest && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Request Details &amp; Approval Timeline</h3>
              <button onClick={() => setSelectedRequest(null)} className="text-slate-400 hover:text-slate-600 font-bold text-xl">&times;</button>
            </div>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl">
                <div><b>Request No:</b> {selectedRequest.request_number}</div>
                <div><b>Purpose:</b> {selectedRequest.purpose}</div>
                <div><b>Est. Budget:</b> ₹ {(selectedRequest.estimated_budget || 0).toLocaleString('en-IN')}</div>
                <div><b>Status:</b> {selectedRequest.status}</div>
              </div>

              {/* Sent Back / Return Remarks Callout */}
              {(selectedRequest.return_remarks || selectedRequest.status === 'returned_for_correction' || selectedRequest.status === 'sent_back') && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs flex items-start gap-2.5">
                  <RotateCcw className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-amber-900">
                      Sent Back for Correction {selectedRequest.sent_back_by ? `by ${selectedRequest.sent_back_by}` : ''}
                      {selectedRequest.sent_back_at && <span className="font-normal text-amber-700 ml-1">({selectedRequest.sent_back_at.slice(0, 10)})</span>}
                    </div>
                    <p className="text-amber-800 mt-0.5 font-medium">{selectedRequest.return_remarks || 'Returned for correction.'}</p>
                  </div>
                </div>
              )}

              {/* Rejection Reason Callout */}
              {(selectedRequest.rejection_reason || selectedRequest.status === 'rejected') && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs flex items-start gap-2.5">
                  <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-rose-900">
                      Rejected {selectedRequest.rejected_by ? `by ${selectedRequest.rejected_by}` : ''}
                      {selectedRequest.rejected_at && <span className="font-normal text-rose-700 ml-1">({selectedRequest.rejected_at.slice(0, 10)})</span>}
                    </div>
                    <p className="text-rose-800 mt-0.5 font-medium">{selectedRequest.rejection_reason || 'Request rejected.'}</p>
                  </div>
                </div>
              )}

              {/* Employee Rework Notes */}
              {selectedRequest.rework_notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs flex items-start gap-2.5">
                  <FileText className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-blue-900">Employee Rework Notes</div>
                    <p className="text-blue-800 mt-0.5">{selectedRequest.rework_notes}</p>
                  </div>
                </div>
              )}

              {/* Reapplication Lineage */}
              {(selectedRequest.is_reapplied || selectedRequest.reapplied_from_id) && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-xs flex items-start gap-2.5">
                  <RefreshCw className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-purple-900">Reapplied Claim</div>
                    <p className="text-purple-800 mt-0.5">
                      Reapplied from rejected request #{selectedRequest.reapplied_from_request_number || selectedRequest.reapplied_from_id}
                    </p>
                  </div>
                </div>
              )}

              {(selectedRequest.advance_requested || selectedRequest.advance_amount > 0) && (
                <div className="bg-amber-50 p-3.5 rounded-xl border border-amber-200 space-y-2">
                  <div className="font-bold text-amber-900 flex justify-between items-center flex-wrap gap-2 border-b border-amber-200 pb-2">
                    <div className="flex items-center gap-3">
                      <span>Advance Requested: <b className="font-mono text-amber-950">₹ {(selectedRequest.requested_advance_amount || selectedRequest.advance_details?.requested_advance_amount || selectedRequest.advance_details?.total_advance || selectedRequest.advance_amount || 0).toLocaleString('en-IN')}</b></span>
                      <span>Advance Approved: <b className="font-mono text-emerald-950 bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200">{selectedRequest.approved_advance_amount !== null && selectedRequest.approved_advance_amount !== undefined ? `₹ ${Number(selectedRequest.approved_advance_amount).toLocaleString('en-IN')}` : (selectedRequest.advance_details?.approved_advance_amount !== null && selectedRequest.advance_details?.approved_advance_amount !== undefined ? `₹ ${Number(selectedRequest.advance_details.approved_advance_amount).toLocaleString('en-IN')}` : 'Pending Approval')}</b></span>
                    </div>
                    <span className="uppercase text-indigo-700 font-bold bg-amber-200/80 px-2 py-0.5 rounded text-[10px]">
                      {(selectedRequest.advance_status === 'paid' || selectedRequest.finance_payment_status === 'paid')
                        ? 'Paid / Disbursed'
                        : (selectedRequest.advance_status === 'payment_pending' || selectedRequest.finance_payment_status === 'pending')
                        ? 'Approved – Pending Finance Payment'
                        : selectedRequest.advance_status?.replace(/_/g, ' ') || selectedRequest.finance_payment_status?.replace(/_/g, ' ') || 'Pending'}
                    </span>
                  </div>

                  {selectedRequest.advance_details && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-700 bg-white/70 p-2 rounded-lg border border-amber-200/60">
                      <div>Hotel: <b>₹ {(selectedRequest.advance_details.hotel_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Food: <b>₹ {(selectedRequest.advance_details.food_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Local Travel: <b>₹ {(selectedRequest.advance_details.local_travel_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Misc: <b>₹ {(selectedRequest.advance_details.misc_amount || 0).toLocaleString('en-IN')}</b></div>
                    </div>
                  )}

                  {(selectedRequest.advance_payment?.payment_status === 'paid' || selectedRequest.finance_payment_status === 'paid' || selectedRequest.advance_paid) && (
                    <div className="text-[11px] text-emerald-900 bg-emerald-50 p-2.5 rounded-lg border border-emerald-200 flex flex-wrap gap-3">
                      <div>Status: <b className="uppercase text-emerald-700">Paid / Disbursed</b></div>
                      <div>Paid Amount: <b>₹ {(selectedRequest.advance_payment?.paid_amount || selectedRequest.finance_payment_details?.paid_amount || selectedRequest.advance_paid_amount || selectedRequest.advance_amount || 0).toLocaleString('en-IN')}</b></div>
                      <div>Disbursement Date: <b>{selectedRequest.advance_payment?.payment_date || selectedRequest.finance_payment_details?.payment_date || 'N/A'}</b></div>
                      <div>UTR / Ref: <b className="font-mono">{selectedRequest.advance_payment?.payment_reference || selectedRequest.finance_payment_details?.utr_number || selectedRequest.advance_payment_ref || 'N/A'}</b></div>
                    </div>
                  )}
                </div>
              )}

              {/* TICKET BOOKING INFORMATION */}
              {(selectedRequest.ticket_booking_status || selectedRequest.ticket_booking_info || selectedRequest.pnr_number || selectedRequest.ticket_not_booked_reason || selectedRequest.status === 'booked') && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-slate-800 uppercase text-[11px] tracking-wide flex items-center gap-1.5">
                      <Plane className="w-3.5 h-3.5 text-indigo-600" /> Ticket Booking Information
                    </h4>
                    {selectedRequest.ticket_booking_status === 'booked' || selectedRequest.status === 'booked' || selectedRequest.ticket_booking_info?.status === 'booked' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-full">
                        <CheckCircle className="w-3 h-3 text-emerald-600" /> Ticket Booked
                      </span>
                    ) : selectedRequest.ticket_booking_status === 'not_booked' || selectedRequest.ticket_booking_info?.status === 'not_booked' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full">
                        <AlertTriangle className="w-3 h-3 text-amber-700" /> Ticket Not Booked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 border border-slate-300 px-2 py-0.5 rounded-full">
                        <Clock className="w-3 h-3 text-slate-500" /> Booking Pending
                      </span>
                    )}
                  </div>

                  {selectedRequest.ticket_booking_status === 'not_booked' || selectedRequest.ticket_booking_info?.status === 'not_booked' ? (
                    <div className="bg-amber-50/80 border border-amber-200 rounded-lg p-2.5 text-[11px] space-y-1">
                      <div className="text-amber-900 font-medium">
                        <span className="font-semibold text-amber-800">Reason for not booking: </span>
                        {selectedRequest.ticket_booking_info?.reason_not_booked || selectedRequest.ticket_not_booked_reason || 'No specific reason recorded.'}
                      </div>
                      <div className="flex flex-wrap gap-4 text-slate-500 text-[10px] pt-1 border-t border-amber-200/60">
                        {selectedRequest.ticket_booking_info?.booking_date && (
                          <span>Recorded On: <b className="text-slate-700">{selectedRequest.ticket_booking_info.booking_date}</b></span>
                        )}
                        {selectedRequest.ticket_booking_info?.booked_by_name && (
                          <span>Recorded By: <b className="text-slate-700">{selectedRequest.ticket_booking_info.booked_by_name}</b></span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-[11px]">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="bg-white p-2 rounded border border-slate-200">
                          <span className="text-slate-500 block text-[10px]">PNR / Ref No</span>
                          <span className="font-mono font-bold text-slate-800">
                            {selectedRequest.ticket_booking_info?.pnr_number || selectedRequest.pnr_number || selectedRequest.booking_reference || 'N/A'}
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded border border-slate-200">
                          <span className="text-slate-500 block text-[10px]">Carrier / Flight / Train</span>
                          <span className="font-semibold text-slate-800">
                            {selectedRequest.ticket_booking_info?.carrier_number || selectedRequest.carrier_number || 'N/A'}
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded border border-slate-200">
                          <span className="text-slate-500 block text-[10px]">Vendor / Agent</span>
                          <span className="font-semibold text-slate-800">
                            {selectedRequest.ticket_booking_info?.vendor_name || selectedRequest.vendor_name || 'N/A'}
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded border border-slate-200">
                          <span className="text-slate-500 block text-[10px]">Total Ticket Cost</span>
                          <span className="font-bold text-emerald-700">
                            ₹ {Number(selectedRequest.ticket_booking_info?.total_fare || selectedRequest.ticket_fare || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>

                      {selectedRequest.ticket_booking_info?.fare_breakdown && (
                        <div className="grid grid-cols-3 gap-2 bg-white/70 p-2 rounded border border-slate-200 text-[10px]">
                          <div>Base Fare: <b>₹ {Number(selectedRequest.ticket_booking_info.fare_breakdown.base_fare || 0).toLocaleString('en-IN')}</b></div>
                          <div>Taxes: <b>₹ {Number(selectedRequest.ticket_booking_info.fare_breakdown.taxes || 0).toLocaleString('en-IN')}</b></div>
                          <div>Fees: <b>₹ {Number(selectedRequest.ticket_booking_info.fare_breakdown.fees || 0).toLocaleString('en-IN')}</b></div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-4 text-[10px] text-slate-500 pt-1">
                        {(selectedRequest.ticket_booking_info?.booking_date || selectedRequest.booked_at) && (
                          <span>Booking Date: <b className="text-slate-700">{selectedRequest.ticket_booking_info?.booking_date || selectedRequest.booked_at?.slice(0, 10)}</b></span>
                        )}
                        {(selectedRequest.ticket_booking_info?.booked_by_name || selectedRequest.booked_by) && (
                          <span>Booked By: <b className="text-slate-700">{selectedRequest.ticket_booking_info?.booked_by_name || selectedRequest.booked_by}</b></span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <h4 className="font-bold text-slate-800 mb-2 uppercase text-[10px]">Approval Audit Timeline</h4>
                <div className="space-y-2 border-l-2 border-blue-500 pl-3">
                  {(selectedRequest.approval_timeline || []).map((t, idx) => (
                    <div key={idx} className="space-y-0.5">
                      <div className="font-bold text-slate-900">{t.actor_name} <span className="font-normal text-slate-500">({t.step})</span></div>
                      <div className="text-slate-500 text-[10px]">{t.timestamp?.slice(0, 19)?.replace('T', ' ')} — <span className="capitalize font-semibold text-blue-600">{t.action}</span></div>
                      {t.comments && <div className="text-slate-600 bg-slate-100 p-1.5 rounded">{t.comments}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SELF BOOKING CLAIM SUBMISSION MODAL */}
      <SelfBookingClaimModal
        isOpen={showSelfBookingModal}
        onClose={() => setShowSelfBookingModal(false)}
        onSuccess={fetchMyRequests}
      />
    </div>
  );
};

export default ESSTravelRequests;
